import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Select, Space, message, Tooltip } from 'antd';
import { ReloadOutlined, CheckOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const TYPE_LABELS_FA = {
  failed_login: 'ورود ناموفق',
  admin_failed_login: 'ورود ناموفق ادمین',
  otp_failed: 'کد OTP اشتباه',
  brute_force: 'brute-force / عبور از سقف درخواست',
  banned_access_attempt: 'دسترسی کاربر مسدود',
  payment_anomaly: 'ناهنجاری پرداخت',
  multi_account_attempt: 'تلاش چندحسابی (Sybil)'
};

const SEVERITY_LABELS_FA = { low: 'کم', medium: 'متوسط', high: 'شدید' };
const SEVERITY_COLORS = { low: 'default', medium: 'gold', high: 'red' };

export default function SecurityLogManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ type: undefined, severity: undefined, isRead: undefined });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getSecurityLogs(page, 30, filters);
      if (res.data?.success) {
        setItems(res.data.logs || []);
        setTotal(res.data.pagination?.total || 0);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (err) {
      message.error('خطا در بارگذاری فعالیت‌های مشکوک');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await adminAPI.markSecurityLogRead(id);
      setItems(prev => prev.map(it => it._id === id ? { ...it, isRead: true } : it));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      message.error('خطا در علامت‌گذاری');
    }
  };

  const markAllRead = async () => {
    try {
      await adminAPI.markAllSecurityLogsRead();
      message.success('همه موارد خوانده‌شده علامت‌گذاری شدند');
      load();
    } catch {
      message.error('خطا در علامت‌گذاری همه موارد');
    }
  };

  const columns = [
    {
      title: 'شدت',
      dataIndex: 'severity',
      key: 'severity',
      width: 90,
      render: s => <Tag color={SEVERITY_COLORS[s] || 'default'}>{SEVERITY_LABELS_FA[s] || s}</Tag>
    },
    {
      title: 'نوع',
      dataIndex: 'type',
      key: 'type',
      width: 180,
      render: t => TYPE_LABELS_FA[t] || t
    },
    {
      title: 'شرح',
      dataIndex: 'message',
      key: 'message',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.user && (
            <div style={{ color: '#888', fontSize: 12 }}>
              کاربر: {record.user.firstName} {record.user.lastName} ({record.user.phoneNumber || record.user.username || '—'})
            </div>
          )}
          {record.admin && (
            <div style={{ color: '#888', fontSize: 12 }}>ادمین: {record.admin.username}</div>
          )}
          {record.ip && <div style={{ color: '#aaa', fontSize: 11 }}>IP: {record.ip}</div>}
        </div>
      )
    },
    {
      title: 'زمان',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: d => new Date(d).toLocaleString('fa-IR')
    },
    {
      title: 'وضعیت',
      dataIndex: 'isRead',
      key: 'isRead',
      width: 110,
      render: (isRead, record) => (
        isRead
          ? <Tag color="gray">بررسی‌شده</Tag>
          : <Button size="small" icon={<CheckOutlined />} onClick={() => markRead(record._id)}>علامت‌گذاری</Button>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Tag color="gold" style={{ padding: '4px 8px', fontSize: 13 }}>
          موارد بررسی‌نشده: {unreadCount}
        </Tag>

        <Select
          allowClear
          placeholder="نوع فعالیت"
          style={{ minWidth: 200 }}
          value={filters.type}
          onChange={v => { setPage(1); setFilters(f => ({ ...f, type: v })); }}
          options={Object.entries(TYPE_LABELS_FA).map(([value, label]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="شدت"
          style={{ minWidth: 140 }}
          value={filters.severity}
          onChange={v => { setPage(1); setFilters(f => ({ ...f, severity: v })); }}
          options={Object.entries(SEVERITY_LABELS_FA).map(([value, label]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="وضعیت بررسی"
          style={{ minWidth: 160 }}
          value={filters.isRead}
          onChange={v => { setPage(1); setFilters(f => ({ ...f, isRead: v })); }}
          options={[{ value: 'false', label: 'بررسی‌نشده' }, { value: 'true', label: 'بررسی‌شده' }]}
        />

        <Space style={{ marginInlineStart: 'auto' }}>
          <Tooltip title="به‌روزرسانی">
            <Button icon={<ReloadOutlined />} onClick={load} />
          </Tooltip>
          <Button onClick={markAllRead} disabled={unreadCount === 0}>علامت‌گذاری همه به‌عنوان بررسی‌شده</Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="_id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: 30,
          total,
          onChange: setPage,
          showSizeChanger: false
        }}
        size="small"
        rowClassName={record => !record.isRead ? 'unread-message-row' : ''}
      />
    </div>
  );
}
